import { ApiResult } from "@/common/api/ApiResult.model";
import { TimeFrameOption } from "@/common/state/models";
import {
  ActionEnum,
  AxisType,
  CoordinateInputTier1Tier2,
  TimeAxisRangeInput,
  targetAxis,
} from "@/gql/types";
import { Subject, InvEntityType } from "../data/model";
import { cord, cord3, formatInput, gql2 } from "@/gql/lib";
import { POPULATION_TYPE, PopulationTypeHelper } from "@/common/models";
import { correctTier12XY, fixTier3XY } from "@/gql/cords";
import { SharedState } from "@/common/state";
import EntitiesTableModalVue from "@/components/modals/entities-table-modal/EntitiesTableModal.vue";
import { EntitiesModalProps } from "@/components/modals/entities-table-modal/model";
import { UseModal } from "@/components/root/modal";

export const get_entities_modal_target_axis = (
  subject_id: string,
  cord: CoordinateInputTier1Tier2
): targetAxis => {
  if (
    (cord.x.value && cord.x.value == subject_id) ||
    (cord.x.values && cord.x.values[0] == subject_id)
  ) {
    return targetAxis.Y;
  }
  return targetAxis.X;
};

const create_get_summary = (
  summary_sentence: (subject: Subject) => string,
  axis_type: AxisType
) =>
  gql2<
    { subject: Subject; relative_time_frame: TimeFrameOption },
    { count: number; relative_count: number; type: InvEntityType; desc: string }
  >(
    "inv-counters.employee-senders",
    ({ subject, relative_time_frame }, { frame }) => {
      const cords = (time_frame: TimeAxisRangeInput) =>
        correctTier12XY({
          coordinate: {
            x: cord(axis_type),
            y: cord(PopulationTypeHelper.asAxisType2(subject.type), [
              subject.id,
            ]),
            z: time_frame,
          },
          action: ActionEnum.UniqCount,
        });
      return {
        query: `{
      delta: tier1count(params:${formatInput(cords(frame))}){
         count
         uniqs
      }
      relative: tier1count(params:${formatInput(
        cords({
          type: relative_time_frame.type,
          values: [relative_time_frame.from],
        })
      )}){
       count
       uniqs
    }
     }`,
        mapper: (res) => {
          const { delta, relative } = res;
          return {
            type: "EMPLOYEE_SENDER",
            count: delta.length > 0 ? delta[0].uniqs : 0,
            relative_count: relative.length > 0 ? relative[0].uniqs : 0,
            desc: summary_sentence(subject),
          };
        },
      };
    }
  );

const _get_main_cords = (axis_type: AxisType, subject: Subject) => {
  const { from, type } = SharedState.selectedTimeFrameOption.value;
  return correctTier12XY({
    coordinate: {
      x: cord(axis_type),
      y: cord(PopulationTypeHelper.asAxisType2(subject.type), [subject.id]),
      z: {
        type,
        values: [from],
      },
    },
  }).coordinate;
};

const _get_events_cords = (
  axis_type: AxisType,
  subject: Subject,
  sub_subject_id: string
) => {
  const { from, type } = SharedState.selectedTimeFrameOption.value;
  return fixTier3XY({
    x: cord3(axis_type, [sub_subject_id]),
    y: cord3(PopulationTypeHelper.asAxisType2(subject.type), [subject.id]),
    z: {
      type,
      values: [from],
    },
  });
};

const create_show_modal = (
  type: POPULATION_TYPE,
  entity_type: InvEntityType,
  axis_type: AxisType,
  get_titles: (
    subject: Subject
  ) => {
    main: string;
    files: (entity: any) => string;
    events: (entity: any) => string;
  }
) => {
  return (subject: Subject) => {
    const titles = get_titles(subject);
    const props: EntitiesModalProps = {
      title: titles.main,
      type,
      info_types: [],
      coordinate: _get_main_cords(axis_type, subject),
      files_title: (entity) => titles.files(entity.name),
      events: {
        coordinate: (entity) => {
          return _get_events_cords(axis_type, subject, entity.id);
        },
        title: (entity) => titles.events(entity.name),
      },
    };
    UseModal().show(EntitiesTableModalVue, props);
  };
};

export const create_entity_type_handler = (payload: {
  modal_type: POPULATION_TYPE;
  entity_type: InvEntityType;
  axis_type: AxisType;
  agg_axis_type: AxisType;
  count_prop: "count" | "files" | "uniqs";
  titles: (
    subject: Subject
  ) => {
    main: string;
    files: (entity: any) => string;
    events: (entity: any) => string;
  };
  summary_sentence: (subject: Subject) => string;
}): {
  get_summary: (input: {
    subject: Subject;
    relative_time_frame: TimeFrameOption;
  }) => Promise<
    ApiResult<{
      count: number;
      relative_count: number;
      type: InvEntityType;
      desc: string;
    }>
  >;
  show_modal: (subject: Subject) => void;
} => {
  return {
    get_summary: create_get_summary(
      payload.summary_sentence,
      payload.agg_axis_type
    ),
    show_modal: create_show_modal(
      payload.modal_type,
      payload.entity_type,
      payload.axis_type,
      payload.titles
    ),
  };
};
